'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Shield, Trophy, LogIn, LogOut, Menu, X, User, LayoutDashboard } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

type NavItem = {
  label: string;
  href: string;
  icon: React.ReactNode;
};

const NAV_ITEMS: NavItem[] = [
  { label: 'Play', href: '/', icon: <Shield size={16} /> },
  { label: 'Scoreboard', href: '/scoreboard', icon: <Trophy size={16} /> },
];

export default function Topbar() {
  const pathname = usePathname();
  const router = useRouter();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, profile, signOut } = useAuth();

  async function handleLogout() {
    try {
      await signOut();
      setMobileOpen(false);
      router.push('/sign-up-login-screen');
      router.refresh();
    } catch {
      // Silently fail
    }
  }

  const displayName = profile?.username || (user as { email?: string } | null)?.email?.split('@')[0] || 'User';
  const isAdmin = profile?.role === 'admin' || (user as { email?: string } | null)?.email === 'tselmuun@infosolution.mn';

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border bg-background/95 backdrop-blur-sm">
      <div className="mx-auto max-w-screen-2xl px-4 lg:px-8">
        <div className="flex h-14 items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 group">
            <AppLogo size={32} />
            <div className="flex flex-col leading-none">
              <span className="font-sans text-sm font-700 text-foreground tracking-tight">
                AdversaryMatch
              </span>
              <span className="font-mono text-[10px] text-muted-foreground tracking-widest uppercase">
                by CrowdStrike
              </span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {NAV_ITEMS.map((item) => {
              const isActive = pathname === item.href;
              return (
                <Link
                  key={`nav-${item.href}`}
                  href={item.href}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-all duration-150
                    ${isActive
                      ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                >
                  {item.icon}
                  {item.label}
                </Link>
              );
            })}
            {isAdmin && (
              <Link
                href="/admin"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-all duration-150
                  ${pathname === '/admin' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
              >
                <LayoutDashboard size={16} />
                Admin
              </Link>
            )}
          </nav>

          {/* Auth area */}
          <div className="hidden md:flex items-center gap-2">
            {user ? (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded bg-muted border border-border">
                  <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center">
                    <User size={11} className="text-primary" />
                  </div>
                  <span className="text-sm font-medium text-foreground">{displayName}</span>
                  {isAdmin && (
                    <span className="text-[9px] font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded border border-primary/30">ADMIN</span>
                  )}
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
                >
                  <LogOut size={14} />
                  Sign out
                </button>
              </div>
            ) : (
              <Link
                href="/sign-up-login-screen"
                className="flex items-center gap-1.5 px-4 py-1.5 rounded bg-primary text-primary-foreground text-sm font-semibold hover:bg-accent transition-all duration-150 active:scale-95"
              >
                <LogIn size={14} />
                Sign in
              </Link>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden border-t border-border bg-card fade-in">
          <div className="px-4 py-3 space-y-1">
            {NAV_ITEMS.map((item) => {
              const isActive = pathname === item.href;
              return (
                <Link
                  key={`mobile-nav-${item.href}`}
                  href={item.href}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded text-sm font-medium transition-all duration-150
                    ${isActive
                      ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                >
                  {item.icon}
                  {item.label}
                </Link>
              );
            })}
            {isAdmin && (
              <Link
                href="/admin"
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-2 px-3 py-2.5 rounded text-sm font-medium transition-all duration-150
                  ${pathname === '/admin' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
              >
                <LayoutDashboard size={16} />
                Admin Dashboard
              </Link>
            )}
            <div className="pt-2 border-t border-border">
              {user ? (
                <div className="space-y-1">
                  <div className="flex items-center gap-2 px-3 py-2">
                    <User size={14} className="text-primary" />
                    <span className="text-sm text-foreground font-medium">{displayName}</span>
                    {isAdmin && (
                      <span className="text-[9px] font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded border border-primary/30">ADMIN</span>
                    )}
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
                  >
                    <LogOut size={14} />
                    Sign out
                  </button>
                </div>
              ) : (
                <Link
                  href="/sign-up-login-screen"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 px-3 py-2.5 rounded bg-primary text-primary-foreground text-sm font-semibold"
                >
                  <LogIn size={14} />
                  Sign in to save scores
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}